import React from "react";
import { Create, Form, Input, Select, DatePicker, useForm, InputNumber, Upload, Button, TimePicker, Modal, Typography, Table, Checkbox, Space } from "@pankod/refine-antd";
import { useNavigation } from "@pankod/refine-core";
import { notification } from "@pankod/refine-antd";
import axios from "@pankod/refine-simple-rest/node_modules/axios";
import { API_URL, TOKEN_KEY } from "../../constants/environment";
import { impactAxes } from "../../constants/workPlan.constants";
import { IWorkPlan } from "../../interfaces/work-plan.interface";
import dayjs from "dayjs";
import { InfoCircleOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { Text, Paragraph, Title } = Typography;

interface BudgetSupply {
    key: string;
    supply_name: string;
    quantity: number;
    unit_cost: number;
    total_cost: number;
    requires_support: boolean;
}

export const WorkPlanCreate: React.FC = () => {
    const { formProps, saveButtonProps } = useForm<IWorkPlan>();
    const [fileList, setFileList] = React.useState<any[]>([]);
    const [axisInfoModalVisible, setAxisInfoModalVisible] = React.useState(false);
    const [budgetSupplies, setBudgetSupplies] = React.useState<BudgetSupply[]>([]);
    const { push } = useNavigation();

    const showAxisInfo = () => {
        setAxisInfoModalVisible(true);
    };

    const addSupply = () => {
        const newSupply: BudgetSupply = {
            key: Date.now().toString(),
            supply_name: "",
            quantity: 0,
            unit_cost: 0,
            total_cost: 0,
            requires_support: false,
        };
        setBudgetSupplies([...budgetSupplies, newSupply]);
    };

    const removeSupply = (key: string) => {
        setBudgetSupplies(budgetSupplies.filter(supply => supply.key !== key));
    };

    const updateSupply = (key: string, field: keyof BudgetSupply, value: any) => {
        setBudgetSupplies(budgetSupplies.map(supply => {
            if (supply.key === key) {
                const updatedSupply = { ...supply, [field]: value };
                
                // Calcular total_cost automáticamente cuando cambian quantity o unit_cost
                if (field === 'quantity' || field === 'unit_cost') {
                    updatedSupply.total_cost = (updatedSupply.quantity || 0) * (updatedSupply.unit_cost || 0);
                }
                
                return updatedSupply;
            }
            return supply;
        }));
    };

    const getTotalBudget = () => {
        return budgetSupplies.reduce((sum, supply) => sum + supply.total_cost, 0);
    };

    const getTotalWithoutSupport = () => {
        return budgetSupplies
            .filter(supply => !supply.requires_support)
            .reduce((sum, supply) => sum + supply.total_cost, 0);
    };

    const getTotalWithSupport = () => {
        return budgetSupplies
            .filter(supply => supply.requires_support)
            .reduce((sum, supply) => sum + supply.total_cost, 0);
    };

    const onFinish = async (values: any) => {
        const formData = new FormData();
        if (fileList.length > 0 && fileList[0].originFileObj) {
            formData.append("budget", fileList[0].originFileObj);
        }

        formData.append("start_date", typeof values.start_date === "string" ? dayjs(values.start_date, "DD/MM/YYYY") : values.start_date.format("YYYY-MM-DD"));
        formData.append("end_date", typeof values.end_date === "string" ? dayjs(values.end_date, "DD/MM/YYYY") : values.end_date.format("YYYY-MM-DD"));
        formData.append("start_time", dayjs(values.start_time).format("HH:mm") || "");
        formData.append("end_time", dayjs(values.end_time).format("HH:mm") || "");
        formData.append("impact_axis", values.impact_axis || "");
        formData.append("general_objective", values.general_objective || "");
        formData.append("goal", values.goal || "");
        formData.append("strategy", values.strategy || "");
        formData.append("audience", values.audience || "");
        formData.append("scope", values.scope || "");
        formData.append("total_budget", getTotalBudget().toString());
        
        // Enviar los insumos como campos individuales en FormData
        budgetSupplies.forEach((supply, index) => {
            formData.append(`budget_items[${index}][supply_name]`, supply.supply_name);
            formData.append(`budget_items[${index}][quantity]`, supply.quantity.toString());
            formData.append(`budget_items[${index}][unit_cost]`, supply.unit_cost.toString());
            formData.append(`budget_items[${index}][total_cost]`, supply.total_cost.toString());
            formData.append(`budget_items[${index}][requires_support]`, supply.requires_support ? '1' : '0');
        });

        const token = localStorage.getItem(TOKEN_KEY);
        try {
            await axios.post(`${API_URL}/work-plans`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            notification.success({
                message: "Plan de trabajo guardado correctamente",
                description: "El registro fue creado exitosamente.",
            });
            push("/work-plans");
        } catch (error: any) {
            notification.error({
                message: "Error al guardar",
                description: error?.response?.data?.message || "Ocurrió un error al guardar el plan de trabajo.",
            });
        }
    };

    const beforeUpload = () => false;

    return (
        <Create saveButtonProps={saveButtonProps} title={"Crear actividad"}>
            <Form {...formProps} layout="vertical" onFinish={onFinish}>
                <Form.Item name="start_date" label="Fecha de Inicio de Actividad" rules={[{ required: true }]}> 
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                    label="Hora de inicio del evento"
                    name="start_time"
                    rules={[{ required: true }]}
                    initialValue={dayjs()}
                >
                    <TimePicker style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
                <Form.Item name="end_date" label="Fecha de Fin" rules={[{ required: true }]}> 
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }}  />
                </Form.Item>
                <Form.Item
                    label="Hora de fin del evento"
                    name="end_time"
                    rules={[
                    { required: true },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                        if (!value || value.isAfter(getFieldValue("start_time"))) {
                            return Promise.resolve();
                        }
                        return Promise.reject(
                            new Error("La hora de fin debe ser posterior a la hora de inicio")
                        );
                        },
                    }),
                    ]}
                    initialValue={dayjs().add(1, "hour")}
                >
                    <TimePicker style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
                <Form.Item name="impact_axis" label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Eje</span>
                        <Button 
                            type="link" 
                            size="small"
                            icon={<InfoCircleOutlined />}
                            onClick={showAxisInfo}
                            style={{ padding: 0, height: 'auto' }}
                            title="Ver información detallada de los ejes"
                        >
                            Ver información de los ejes
                        </Button>
                    </div>
                } rules={[{ required: true }]}> 
                    <Select 
                        options={impactAxes.map(axis => ({ label: axis.name, value: axis.id }))} 
                        showSearch
                        optionFilterProp="label"
                        placeholder="Selecciona un eje de trabajo"
                    />
                </Form.Item>
                <Form.Item name="general_objective" label="Objetivo general" rules={[{ required: true }]} style={{ gridColumn: 'span 5' }}>
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="goal" label="Meta" rules={[{ required: true }]} style={{ gridColumn: 'span 5' }}>
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="strategy" label="Estrategia" rules={[{ required: true }]}> 
                    <Input />
                </Form.Item>
                <Form.Item name="audience" label="Público" rules={[{ required: true }]}> 
                    <Input />
                </Form.Item>
                <Form.Item name="scope" label="Alcance" rules={[{ required: true }]} style={{ gridColumn: 'span 5' }}>
                    <Input.TextArea rows={2} />
                </Form.Item>
                
                {/* Sección de Presupuesto */}
                <div style={{ gridColumn: 'span 5', marginTop: '20px' }}>
                    <Title level={4}>Presupuesto - Insumos</Title>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Button 
                            type="default" 
                            onClick={addSupply} 
                            icon={<PlusOutlined />}
                            style={{ width: '100%', backgroundColor: '#2b873a', color: '#ffffff' }}
                        >
                            Agregar Insumo
                        </Button>
                        
                        {budgetSupplies.length > 0 && (
                            <Table
                                dataSource={budgetSupplies}
                                pagination={false}
                                bordered
                                size="middle"
                                scroll={{ x: 800 }}
                                summary={() => (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                <Text strong>Cubierto por UO:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#52c41a' }}>
                                                    ${getTotalWithoutSupport().toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                <Text strong>Con Apoyo Vicerrectoría:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    ${getTotalWithSupport().toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                        <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                <Text strong style={{ fontSize: '16px' }}>Total Presupuesto:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                                    ${getTotalBudget().toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                            >
                                <Table.Column
                                    title="Nombre del Insumo"
                                    dataIndex="supply_name"
                                    key="supply_name"
                                    width={250}
                                    render={(text, record: BudgetSupply) => (
                                        <Input
                                            value={text}
                                            onChange={(e) => updateSupply(record.key, 'supply_name', e.target.value)}
                                            placeholder="Ingrese nombre del insumo"
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Cantidad"
                                    dataIndex="quantity"
                                    key="quantity"
                                    width={120}
                                    render={(text, record: BudgetSupply) => (
                                        <InputNumber
                                            value={text}
                                            onChange={(value) => updateSupply(record.key, 'quantity', value || 0)}
                                            min={0}
                                            step={1}
                                            precision={0}
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Costo Unitario"
                                    dataIndex="unit_cost"
                                    key="unit_cost"
                                    width={150}
                                    render={(text, record: BudgetSupply) => (
                                        <InputNumber
                                            value={text}
                                            onChange={(value) => updateSupply(record.key, 'unit_cost', value || 0)}
                                            prefix="$"
                                            min={0}
                                            step={0.01}
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Costo Total"
                                    dataIndex="total_cost"
                                    key="total_cost"
                                    width={150}
                                    render={(text) => (
                                        <Text strong style={{ textAlign: 'center', display: 'block' }}>${text.toFixed(2)}</Text>
                                    )}
                                />
                                <Table.Column
                                    title="Apoyo Vicerrectoría"
                                    dataIndex="requires_support"
                                    key="requires_support"
                                    width={150}
                                    align="center"
                                    render={(checked, record: BudgetSupply) => (
                                        <Checkbox
                                            checked={checked}
                                            onChange={(e) => updateSupply(record.key, 'requires_support', e.target.checked)}
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Acciones"
                                    key="actions"
                                    width={100}
                                    align="center"
                                    render={(_, record: BudgetSupply) => (
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeSupply(record.key)}
                                        />
                                    )}
                                />
                            </Table>
                        )}
                    </Space>
                </div>
            </Form>

            <Modal
                title="Información de los Ejes de Trabajo"
                visible={axisInfoModalVisible}
                onCancel={() => setAxisInfoModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setAxisInfoModalVisible(false)}>
                        Entendido
                    </Button>
                ]}
                width={800}
                style={{ top: 20 }}
            >
                <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}>
                    {impactAxes.map((axis, index) => (
                        <div key={axis.id} style={{ marginBottom: index < impactAxes.length - 1 ? '32px' : '0' }}>
                            <Title level={4} style={{ color: '#1890ff', marginBottom: '12px' }}>
                                {axis.name}
                            </Title>
                            <div 
                                style={{ textAlign: 'justify', lineHeight: '1.6' }}
                                dangerouslySetInnerHTML={{ __html: axis.description }}
                            />
                            {index < impactAxes.length - 1 && (
                                <div style={{ borderBottom: '1px solid #f0f0f0', margin: '24px 0' }} />
                            )}
                        </div>
                    ))}
                </div>
            </Modal>
        </Create>
    );
};

export default WorkPlanCreate;
